// src/infrastructure/integration/integrationMapper.ts
import type {
  DianDetails,
  EmailDetails,
  ErpDetails,
  ForwardingStatus,
  Integration,
  IntegrationAvailability,
  IntegrationCategory,
  IntegrationError,
  IntegrationStatus,
  SiiDetails,
  SharedFrom,
  SiiEntityStatus,
  SlackDetails,
  SocietyHealth,
  TestResult,
} from '@/domain/integration/Integration'
import { isIntegrationKey } from '@/domain/integration/Integration'

export interface RawForwarding {
  sender_email: string
  status: ForwardingStatus
  accepted_at: string | null
  confirmation_code: string | null
}

export interface RawSharedFrom { society_id: number, name: string }

export interface RawSiiEntity { rut: string | null, validated: boolean, last_verified_at: string | null }

// Every source's keys in one optional bag: the row is read by the entry's `key`, which a union
// of raw shapes could not be narrowed by without a cast per branch.
export interface RawSocietyDetails {
  credential_id?: number | null
  certificate_credential_id?: number | null
  company_connected?: boolean
  person_connected?: boolean
  uses_public_invoicing_entity?: boolean | null
  company?: RawSiiEntity | null
  person?: RawSiiEntity | null
  rfc?: string | null
  reception_connected?: boolean
  alanube_registered?: boolean
  test_set_status?: DianDetails['testSetStatus']
  address?: string | null
  received_30d?: number
  ok_30d?: number
  failed_30d?: number
  forwarding?: RawForwarding | null
  failed_reports_30d?: number
  completed_reports_30d?: number
  shared_from?: RawSharedFrom | null
}

export interface RawTenantDetails {
  last_ok_at?: string | null
  last_error_code?: string | null
  erp?: string | null
  failed_payments_30d?: number
}

export interface RawSociety {
  society_id: number
  name: string
  national_id: string | null
  country: string | null
  status: IntegrationStatus
  last_success_at: string | null
  last_attempt_at: string | null
  error: IntegrationError | null
  cert_expires_at: string | null
  details: RawSocietyDetails | null
}

export interface RawIntegration {
  key: string
  category: IntegrationCategory
  scope: 'society' | 'tenant'
  availability: IntegrationAvailability
  read_only: boolean
  status: IntegrationStatus
  status_detail?: IntegrationError | null
  societies: RawSociety[]
  details: RawTenantDetails | null
}

export interface RawTest { ok: boolean, error_code: string | null }

function toEmailDetails(raw: RawSocietyDetails): EmailDetails {
  const fwd = raw.forwarding
  return {
    address: raw.address ?? null,
    received30d: raw.received_30d ?? 0,
    ok30d: raw.ok_30d ?? 0,
    failed30d: raw.failed_30d ?? 0,
    forwarding: fwd
      ? {
          senderEmail: fwd.sender_email,
          status: fwd.status,
          acceptedAt: fwd.accepted_at ?? null,
          confirmationCode: fwd.status === 'FAILED' ? fwd.confirmation_code ?? null : null,
        }
      : null,
  }
}

function toSiiEntity(raw: RawSiiEntity | null | undefined): SiiEntityStatus | null {
  if (!raw) return null
  return { rut: raw.rut ?? null, validated: raw.validated ?? false, lastVerifiedAt: raw.last_verified_at ?? null }
}

function toSiiDetails(raw: RawSocietyDetails): SiiDetails {
  return {
    credentialId: raw.credential_id ?? null,
    certificateCredentialId: raw.certificate_credential_id ?? null,
    companyConnected: raw.company_connected ?? false,
    personConnected: raw.person_connected ?? false,
    usesPublicInvoicingEntity: raw.uses_public_invoicing_entity ?? null,
    company: toSiiEntity(raw.company),
    person: toSiiEntity(raw.person),
  }
}

function toSharedFrom(raw: RawSharedFrom | null | undefined): SharedFrom | null {
  return raw ? { societyId: raw.society_id, name: raw.name } : null
}

function toSocietyDetails(key: string, raw: RawSocietyDetails) {
  switch (key) {
    case 'sii': return toSiiDetails(raw)
    case 'sat': return { rfc: raw.rfc ?? null, credentialId: raw.credential_id ?? null }
    case 'dian': return {
      credentialId: raw.credential_id ?? null,
      receptionConnected: raw.reception_connected ?? false,
      alanubeRegistered: raw.alanube_registered ?? false,
      testSetStatus: raw.test_set_status ?? null,
    }
    case 'sunat': return { credentialId: raw.credential_id ?? null }
    case 'clara': return { sharedFrom: toSharedFrom(raw.shared_from) }
    case 'tusdatos': return {
      failedReports30d: raw.failed_reports_30d ?? 0,
      completedReports30d: raw.completed_reports_30d ?? 0,
      sharedFrom: toSharedFrom(raw.shared_from),
    }
    case 'invoicing_email': return toEmailDetails(raw)
    default: return {}
  }
}

function toTenantDetails(key: string, raw: RawTenantDetails | null): SlackDetails | ErpDetails | null {
  if (!raw) return null
  if (key === 'slack') return { lastOkAt: raw.last_ok_at ?? null, lastErrorCode: raw.last_error_code ?? null }
  if (key === 'erp') return { erp: raw.erp ?? null, failedPayments30d: raw.failed_payments_30d ?? 0 }
  return null
}

function toSociety(key: string, raw: RawSociety): SocietyHealth<ReturnType<typeof toSocietyDetails>> {
  return {
    societyId: raw.society_id,
    name: raw.name,
    nationalId: raw.national_id ?? null,
    country: raw.country ?? null,
    status: raw.status,
    lastSuccessAt: raw.last_success_at ?? null,
    lastAttemptAt: raw.last_attempt_at ?? null,
    error: raw.error ?? null,
    certExpiresAt: raw.cert_expires_at ?? null,
    details: toSocietyDetails(key, raw.details ?? {}),
  }
}

export function toIntegrations(raw: RawIntegration[]): Integration[] {
  return raw.filter((r) => isIntegrationKey(r.key)).map(toIntegration)
}

export function toIntegration(raw: RawIntegration): Integration {
  const base = {
    key: raw.key,
    category: raw.category,
    availability: raw.availability,
    readOnly: raw.read_only,
    status: raw.status,
    statusDetail: raw.status_detail ?? null,
  }
  if (raw.scope === 'tenant') return { ...base, scope: 'tenant', details: toTenantDetails(raw.key, raw.details) } as Integration
  return { ...base, scope: 'society', societies: (raw.societies ?? []).map((s) => toSociety(raw.key, s)) } as Integration
}

export function toTest(raw: RawTest): TestResult {
  return { ok: raw.ok, errorCode: raw.ok ? null : raw.error_code ?? null }
}
